import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { EventGuestImportPreview, EventGuestImportResult, EventGuestListResponse, EventGuestModel } from './event-guest.model';
import { ResourceData } from '@app/shared/abstract/resource.data';

@Injectable({
  providedIn: 'root',
})
export class EventGuestService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getGuests(eventId: number | string): Observable<EventGuestListResponse> {
    return this.http.get<EventGuestListResponse>(`${this.apiUrl}/api/events/${eventId}/guests`);
  }

  downloadTemplate(eventId: number | string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/api/events/${eventId}/guests/import-template`, { responseType: 'blob' });
  }

  previewImport(eventId: number | string, formData: FormData): Observable<EventGuestImportPreview> {
    return this.http.post<EventGuestImportPreview>(`${this.apiUrl}/api/events/${eventId}/guests/import/preview`, formData);
  }

  importGuests(eventId: number | string, formData: FormData): Observable<EventGuestImportResult> {
    return this.http.post<EventGuestImportResult>(`${this.apiUrl}/api/events/${eventId}/guests/import`, formData);
  }

  createGuest(eventId: number | string, payload: Record<string, any>): Observable<ResourceData<EventGuestModel>> {
    return this.http.post<ResourceData<EventGuestModel>>(`${this.apiUrl}/api/events/${eventId}/guests`, payload);
  }

  updateGuest(eventId: number | string, guestId: number, payload: Record<string, any>): Observable<ResourceData<EventGuestModel>> {
    return this.http.put<ResourceData<EventGuestModel>>(`${this.apiUrl}/api/events/${eventId}/guests/${guestId}`, payload);
  }

  deleteGuest(eventId: number | string, guestId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/api/events/${eventId}/guests/${guestId}`);
  }

  updateDietary(eventId: number | string, guestId: number, payload: Record<string, any>): Observable<ResourceData<EventGuestModel>> {
    return this.http.put<ResourceData<EventGuestModel>>(`${this.apiUrl}/api/events/${eventId}/guests/${guestId}/dietary`, payload);
  }

  // TVG-41: inline row edits for age group, type of guest and menu. Each hits a
  // dedicated endpoint that reuses the full-record validation and permissions.
  updateGuestType(eventId: number | string, guestId: number, guestType: string): Observable<ResourceData<EventGuestModel>> {
    return this.http.patch<ResourceData<EventGuestModel>>(
      `${this.apiUrl}/api/events/${eventId}/guests/${guestId}/guest-type`,
      { guest_type: guestType },
    );
  }

  updateAttendance(eventId: number | string, guestId: number, attendance: string): Observable<ResourceData<EventGuestModel>> {
    return this.http.patch<ResourceData<EventGuestModel>>(
      `${this.apiUrl}/api/events/${eventId}/guests/${guestId}/attendance`,
      { attendance },
    );
  }

  updateMenuPreference(eventId: number | string, guestId: number, menuPreference: string | null): Observable<ResourceData<EventGuestModel>> {
    return this.http.patch<ResourceData<EventGuestModel>>(
      `${this.apiUrl}/api/events/${eventId}/guests/${guestId}/menu-preference`,
      { menu_preference: menuPreference },
    );
  }

  markAccommodation(eventId: number | string, guestIds: number[]): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.apiUrl}/api/events/${eventId}/guests/accommodation`, { guest_ids: guestIds });
  }

  updateAccommodation(eventId: number | string, guestId: number, requiresAccommodation: boolean): Observable<ResourceData<EventGuestModel>> {
    return this.http.put<ResourceData<EventGuestModel>>(
      `${this.apiUrl}/api/events/${eventId}/guests/${guestId}/accommodation`,
      { requires_accommodation: requiresAccommodation },
    );
  }
}
